export const MAP_HELP = [
  {
    title: "The ATM Map",
    body: "Every marker is one ATM. Its color tells you the latest reported status for withdrawals — the most common use case.",
  },
  {
    title: "Marker Colors",
    body: "Green = Working. Red = Out of Service. Yellow = Out of Cash. Grey = Stale (no recent reports — the status is old and may not be accurate anymore).",
  },
  {
    title: "Deposit Pip",
    body: "If a small green/yellow/red dot appears on the top-left of a marker, that ATM also accepts deposits. The pip's color shows the latest deposit status independently from withdrawals — the same machine can be working for withdrawals but offline for deposits.",
  },
  {
    title: "Confidence & Freshness",
    body: "Brighter markers = more recent reports. Faded markers mean it's been a while since anyone reported, so take the status with a grain of salt.",
  },
  {
    title: "Tap an ATM",
    body: "Tap any marker to open its details — bank, address, reliability, recent reports, and split withdraw/deposit status when available.",
  },
  {
    title: "Report a Status",
    body: "At an ATM right now? Tap it and hit 'Report'. Choose Withdraw or Deposit, the status, and the line length. Up to 2 reports per ATM per hour.",
  },
  {
    title: "Watch an ATM",
    body: "Hit the bell icon on any ATM to watch it. You'll get a notification when its status changes — so you know before you leave the house.",
  },
];

export const NEARBY_HELP = [
  {
    title: "Nearby ATMs",
    body: "ATMs close to you, sorted by your chosen filter. The quickest way to find what's near and working.",
  },
  {
    title: "Sort Options",
    body: "Nearest = closest to you. Most Active = the ones with the most recent reports (so you know the info is fresh).",
  },
  {
    title: "Most Reliable (Pro)",
    body: "Sorts by historical uptime — the ATMs that are almost always working when people check. Pro feature because it crunches 30 days of report data.",
  },
  {
    title: "Status Shown",
    body: "Each card shows the headline status (driven by withdrawals — the primary use case). For deposit-specific status, tap the card to open full details.",
  },
];

export const PROFILE_HELP = [
  {
    title: "Your Profile",
    body: "Your TalaDraw home base — stats, badges, and account settings all in one place.",
  },
  {
    title: "Trust Score",
    body: "Your trust score (0–100) reflects how reliable your reports are. Starts at 50 and rises when your reports match what others see. Higher trust = your reports carry more weight when we calculate ATM status.",
  },
  {
    title: "Badges",
    body: "Earn badges by contributing — first report, consistent reporting, and more. They recognize the people keeping this community accurate.",
  },
  {
    title: "Email Notifications",
    body: "Toggle this on to get email alerts when your watched ATMs change status. Pro members get instant alerts; free users receive them after a short delay.",
  },
  {
    title: "Pro = Ad-Free",
    body: "Pro members see zero ads anywhere in the app, get instant email alerts, can watch up to 10 ATMs (vs 2 for free), and unlock historical insights and per-capability reliability scores.",
  },
];

export const WATCHED_HELP = [
  {
    title: "Your Watched ATMs",
    body: "ATMs you've chosen to keep an eye on. When their status changes, you'll be notified.",
  },
  {
    title: "Watch Limits",
    body: "Free accounts can watch up to 2 ATMs. Pro members watch up to 10. Pick the ones you actually use.",
  },
  {
    title: "Alert Capability Filter",
    body: "Use the Both / Withdrawals / Deposits pills at the top to filter what kind of status changes you get pinged about. Useful if you only ever withdraw — you won't get pinged when the deposit slot acts up.",
  },
  {
    title: "Notifications",
    body: "When a watched ATM goes from Working to Out of Service (or vice versa) for the capability you've selected, you'll get a notification. Pro members get instant email alerts; free users get them with a short delay.",
  },
  {
    title: "Removing a Watch",
    body: "Tap the bell icon on any watched ATM to stop watching it. You can always add it back later.",
  },
];

export const ATM_DETAIL_HELP = [
  {
    title: "ATM Details",
    body: "Everything we know about this ATM — current status, reliability, and recent community reports — split by withdrawal vs deposit when both are tracked.",
  },
  {
    title: "Headline Status",
    body: "The big status badge next to the bank name follows the withdraw status (the primary use case for ~95% of users). When deposit data exists AND its status differs, a smaller secondary chip appears beside it (e.g. ↓ Deposit Out of Service).",
  },
  {
    title: "Capability Rows",
    body: "If both Withdraw and Deposit have been reported recently, you'll see two rows under the stats — one per capability. Each row's color reflects that specific capability's latest status. Capabilities with no data are hidden.",
  },
  {
    title: "Confidence",
    body: "High / Medium / Low / Stale. We weight the most recent reports by how trusted the reporters are and how recent the report is. Confidence tells you how much data backs the headline status.",
  },
  {
    title: "Reliability Score (Pro)",
    body: "Free users see a locked icon; Pro users see the percentage. When both capabilities have enough data (≥3 reports in 30 days), it shows split as 'W X% · D Y%'. A 90% withdraw / 50% deposit ATM is very different from a flat 70%.",
  },
  {
    title: "Submit Report",
    body: "Two-step form: pick Withdraw or Deposit, then pick the status. 'No Cash' is hidden when Deposit is selected (deposits can't run out of cash). Optional 100-character public comment lets you add context — JMD-only, ABM down, long line, etc.",
  },
  {
    title: "Recent Reports",
    body: "The latest 5 reports show the status, the capability pill (Withdraw/Deposit), and any comment the reporter added. Tap helps you see whether the conflict is real or just the queue mix.",
  },
  {
    title: "Multiple ATMs at One Branch",
    body: "When a branch has 2+ machines side-by-side, the map marker shows a small count badge (e.g. 3) instead of stacking duplicate pins. Tap it to open this panel with a quick-switch pill row — pick the exact machine you're at (Drive-thru, Lobby #1, ABM Outside, etc.).",
  },
  {
    title: "Switching Between Sibling ATMs",
    body: "Use the green pill row labeled 'N ATMs at this branch' to flip between machines. The pill you're viewing is highlighted; tap any other to swap. Each machine has its own status, line length, and report history — confirm the right one before you submit.",
  },
];

export const PREMIUM_HELP = [
  {
    title: "Pro Insights",
    body: "Your advanced dashboard — historical patterns and per-capability intelligence on the ATMs you care about.",
  },
  {
    title: "Reliability Rankings",
    body: "Top ATMs by uptime over the last 30 days. The pill row at the top lets you slice by Withdraw, Deposit, or All. Default = Withdraw. ATMs without enough data for the chosen capability are hidden — they'd otherwise bury the genuinely reliable ones.",
  },
  {
    title: "Line Prediction",
    body: "Predicts likely line length (No Line / Short / Long) by time of day and day of the week, based on historical reports. Helpful for avoiding peak hours.",
  },
  {
    title: "Availability Forecast",
    body: "Shows the chance of this ATM being working in your selected window. Use the Withdraw / Deposit toggle on the card to switch between capabilities — particularly useful when an ATM is reliable for withdrawals but flaky for deposits (or vice versa).",
  },
  {
    title: "Date Range",
    body: "Adjust the window — last 7 days, 30, 90, or custom. More data = more accurate predictions, but recent data reflects today's reality better.",
  },
  {
    title: "Reliability On Each ATM",
    body: "When you open an ATM's detail sheet as a Pro user, you'll see 'W X% · D Y%' if both capabilities have enough data (≥3 reports in 30 days). Below that threshold, only the overall % is shown.",
  },
];

export const SUBMIT_ATM_HELP = [
  {
    title: "Submit a New ATM",
    body: "Know of an ATM that's not on our map? Add it here. Once submitted, an admin reviews and approves it before it goes live.",
  },
  {
    title: "Bank Name",
    body: "Select the bank that operates this ATM. If yours isn't listed, choose 'Other' and type it in.",
  },
  {
    title: "Address Fields",
    body: "Enter the street name, the town or area, and select the parish. This helps other users find the ATM and helps us organize geographic data.",
  },
  {
    title: "Coordinates",
    body: "Latitude and longitude are obtained automatically when you enable GPS. Just tap 'Use My Location' and we'll pinpoint where you are.",
  },
  {
    title: "Photo",
    body: "A photo of the ATM helps admins verify the submission. Snap one showing the machine and any bank branding on it.",
  },
];

export const TRUSTED_HELP = [
  {
    title: "Trusted Network",
    body: "A private circle of people you trust (family, coworkers, neighbours). Their ATM reports get shared with each other in real time so you all know what's working before you leave the house.",
  },
  {
    title: "Create a Circle",
    body: "Tap 'New Circle', give it a name (e.g. 'Family', 'Work Crew'), and you become the owner. Owners can invite, remove members, and delete the circle.",
  },
  {
    title: "Invite by Email",
    body: "Open a circle, tap 'Invite', type an email. The invitee gets an email with a link to accept. If they're not registered yet, they can sign up using the same email and the invite waits in their pending list.",
  },
  {
    title: "Invite by Share Link",
    body: "Don't have someone's email? Tap 'Copy share link' inside an invite card to generate a one-time URL anyone can use to join. The link is single-use and expires after 7 days for safety.",
  },
  {
    title: "How Sharing Works",
    body: "When a circle member submits a report within 2km of where another member currently is, that report is delivered to the nearby member as a notification. Reports older than an hour aren't re-shared, so you won't get spammed with stale info.",
  },
  {
    title: "Circle Pulse",
    body: "Inside any circle, you'll see a small strip showing how many reports have been shared and delivered in the last 7 days. If it's quiet, invite more people who use ATMs in your usual areas.",
  },
  {
    title: "Privacy",
    body: "Only members of a circle see each other's shared reports. Reports are only shared if you're physically near another member at the time — no location history is stored or visible to anyone.",
  },
  {
    title: "Leaving or Removing",
    body: "Tap a circle to open it. Non-owners can leave at any time. Owners can remove individual members or delete the entire circle (deleting is permanent and unhinges every invite).",
  },
];
